import { IAuthService } from './IAuthService'
import { IAuthRepository } from '../repositories/IAuthRepository'
import { User, AuthResponse, AuthError, UserProfile } from '../models/User'
import { SESSION_EXPIRED_MESSAGE } from '../../utils/sessionErrors'

export class AuthService implements IAuthService {
  constructor(private authRepository: IAuthRepository) {}

  async signIn(email: string, password: string): Promise<AuthResponse> {
    return this.authRepository.signIn(email, password)
  }

  async signUp(username: string, email: string, password: string, profile?: Partial<UserProfile>): Promise<AuthResponse> {
    return this.authRepository.signUp(username, email, password, profile)
  }

  async signOut(): Promise<void> {
    await this.authRepository.signOut()
  }

  async getCurrentUser(): Promise<User | null> {
    return this.authRepository.getCurrentUser()
  }

  async isAuthenticated(): Promise<boolean> {
    return this.authRepository.isAuthenticated()
  }

  async refreshSession(): Promise<boolean> {
    return this.authRepository.refreshSession()
  }

  async validateEmail(email: string): Promise<boolean> {
    return this.authRepository.validateEmail(email)
  }

  async resetPassword(email: string): Promise<void> {
    await this.authRepository.resetPassword(email)
  }

  async updateProfile(userId: string, profile: Partial<UserProfile>): Promise<UserProfile> {
    return this.authRepository.updateProfile(userId, profile)
  }

  async getProfile(userId: string): Promise<UserProfile | null> {
    return this.authRepository.getProfile(userId)
  }

  async createProfile(userId: string, profile: Partial<UserProfile>): Promise<UserProfile> {
    return this.authRepository.createProfile(userId, profile)
  }

  async checkSession(): Promise<User | null> {
    try {
      const currentUser = await this.getCurrentUser()
      if (!currentUser) return null

      // Validate session is still active
      const isValid = await this.refreshSession()
      if (!isValid) {
        this.handleSessionExpiration()
        return null
      }

      return currentUser
    } catch (error) {
      this.handleSessionExpiration()
      return null
    }
  }

  handleSessionExpiration(): void {
    // Dispatch session expired event
    const event = new CustomEvent('sessionExpired', {
      detail: { message: SESSION_EXPIRED_MESSAGE }
    })
    window.dispatchEvent(event)
  }
} 